import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { SwipeableDrawer, Typography, Divider, Grid, Slide } from '@material-ui/core'
import { FilterSearch as FilterIcon, CloseCircle } from '@refera/ui-icons'
import Theme from '@refera/ui-core'
import { Button, IconButton } from '@refera/ui-web'

import { getAgenciesSimple } from '_modules/agency/actions'
import { agenciesSimpleSelector } from '_modules/agency/selectors'
import { getInspectionTradesmen } from '_modules/inspectionBudget/actions'
import { getInspectionTradesmenSelector } from '_modules/inspectionBudget/selector'

import Autocomplete from '_components/inputs/Autocomplete'
import BasicInput from '_components/inputs/BasicInput'

import { FILTERS, FILTER_OPTIONS, DEFAULT_FILTER, INSURANCE_AREA_OF_ACTIVITY } from './constants'
import useStyles from './styles'

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="right" ref={ref} {...props} />
))

const InspectionBudgetFilter = ({ budgetType, open, onClose, onApply, defaultValues = {} }) => {
  const styles = useStyles()
  const dispatch = useDispatch()

  const agencies = useSelector(agenciesSimpleSelector)
  const tradesmen = useSelector(getInspectionTradesmenSelector)

  const methods = useForm({
    defaultValues: defaultValues || {},
  })

  const { handleSubmit, reset, setValue } = methods

  useEffect(() => {
    if (open) {
      dispatch(
        getAgenciesSimple(
          budgetType === 'insurance' ? { area_of_activity: INSURANCE_AREA_OF_ACTIVITY } : undefined
        )
      )
      if (budgetType === 'inspection') {
        dispatch(getInspectionTradesmen({ pageSize: 1000 }))
      }
    }
  }, [open, dispatch, budgetType])

  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        setValue(key, value)
      })
    }
  }, [defaultValues, setValue])

  const onSubmit = data => {
    onApply(data)
    onClose()
  }

  const handleClear = () => {
    reset(DEFAULT_FILTER)
  }

  const agencyOptions = useMemo(() => {
    return (
      agencies?.map(agency => ({
        label: agency.name,
        value: agency.id,
      })) || []
    )
  }, [agencies])

  const providerOptions = useMemo(() => {
    if (!tradesmen) return []

    const tradesmenData =
      tradesmen && typeof tradesmen.toJS === 'function' ? tradesmen.toJS() : tradesmen
    const list = Array.isArray(tradesmenData) ? tradesmenData : tradesmenData?.results || []

    return list.map(provider => ({
      label: provider?.name || '',
      value: provider?.id || '',
    }))
  }, [tradesmen])

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      onOpen={onClose}
      className={styles.container}
      TransitionComponent={Transition}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <Grid className={styles.header}>
            <Grid className={styles.headerTitle}>
              <Grid className={styles.iconWrapper}>
                <FilterIcon color={Theme.Colors.Primary.Base} className={styles.icon} />
              </Grid>
              <Typography component="h2" variant="h5" className={styles.title}>
                Filtros
              </Typography>
            </Grid>
            <IconButton onClick={onClose} variant="ghost">
              <CloseCircle color={Theme.Colors.Primary.Base} className={styles.closeIcon} />
            </IconButton>
          </Grid>
          <Divider className={styles.divider} />

          <Grid className={styles.content}>
            {FILTERS(budgetType).map(filter => {
              if (filter.type === 'text') {
                return (
                  <BasicInput
                    key={filter.name}
                    name={filter.name}
                    label={filter.label}
                    style={{ marginBottom: 16 }}
                    defaultValue={defaultValues?.[filter.name] || ''}
                  />
                )
              }
              if (filter.name === FILTER_OPTIONS.AGENCY_ID) {
                return (
                  <Autocomplete
                    key={filter.name}
                    name={filter.name}
                    label={filter.label}
                    options={agencyOptions}
                    getOptionLabel={option => option.label}
                    style={{ marginBottom: 16 }}
                    multiple={false}
                    defaultValue={defaultValues?.[filter.name] || ''}
                  />
                )
              }
              if (filter.name === FILTER_OPTIONS.TRADESMAN_ID) {
                return (
                  <Autocomplete
                    key={filter.name}
                    name={filter.name}
                    label={filter.label}
                    options={providerOptions}
                    getOptionLabel={option => option.label}
                    style={{ marginBottom: 16 }}
                    multiple={false}
                    defaultValue={defaultValues?.[filter.name] || ''}
                  />
                )
              }
              return null
            })}
          </Grid>

          <Grid className={styles.buttonsContainer}>
            <Button variant="ghost" color="primary" onClick={handleClear}>
              Limpar filtros
            </Button>
            <Button variant="primary" color="orange" type="submit">
              Aplicar filtro
            </Button>
          </Grid>
        </form>
      </FormProvider>
    </SwipeableDrawer>
  )
}

export default InspectionBudgetFilter
